import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, X, SlidersHorizontal, PlusCircle } from 'lucide-react';
import { useTypesenseSequenceFilter } from '@/hooks/useTypesenseSequenceFilter';
import { SequenceFilterSidebar } from '@/components/sequences/SequenceFilterSidebar';
import { SequenceCard } from '@/components/sequences/SequenceCard';
import { PAGINATION, SEARCH } from '@/constants/app.constants';

interface SearchFilters {
  query: string;
  software: string[];
  holiday: string[];
  genres: string[];
  artists: string[];
  minPrice?: number;
  maxPrice?: number;
  featured?: boolean;
  isFree?: boolean;
  fileFormats?: string[];
  albums?: string[];
  sort: string;
}

interface ProfileSequencesTabProps {
  userId: string;
  username: string;
  isOwner?: boolean;
  isPublicView?: boolean;
}

export const ProfileSequencesTab: React.FC<ProfileSequencesTabProps> = ({
  userId,
  username,
  isOwner = false,
  isPublicView = false
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showFilters, setShowFilters] = useState(false);
  const [artistSearchQuery, setArtistSearchQuery] = useState('');
  const [genreSearchQuery, setGenreSearchQuery] = useState('');
  const [albumSearchQuery, setAlbumSearchQuery] = useState('');
  
  // Initialize filters from URL parameters
  const [filters, setFilters] = useState<SearchFilters>(() => ({
    query: searchParams.get('q') || '',
    software: searchParams.get('software')?.split(',').filter(Boolean) || [],
    holiday: searchParams.get('holiday')?.split(',').filter(Boolean) || [],
    genres: searchParams.get('genres')?.split(',').filter(Boolean) || [],
    artists: searchParams.get('artists')?.split(',').filter(Boolean) || [],
    minPrice: searchParams.get('minPrice') ? Number(searchParams.get('minPrice')) : undefined,
    maxPrice: searchParams.get('maxPrice') ? Number(searchParams.get('maxPrice')) : undefined,
    featured: searchParams.get('featured') === 'true' || undefined,
    isFree: searchParams.get('free') === 'true' || undefined,
    fileFormats: searchParams.get('fileFormats')?.split(',').filter(Boolean) || [],
    albums: searchParams.get('albums')?.split(',').filter(Boolean) || [],
    sort: searchParams.get('sort') || SEARCH.DEFAULT_SORT,
  }));

  const [facetsExpanded, setFacetsExpanded] = useState({
    software: true,
    holiday: true,
    genres: true,
    artists: false,
    albums: false,
    price: true,
    other: false,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = PAGINATION.DEFAULT_PAGE_SIZE;

  // Use Typesense search hook with user_id filter
  const { 
    sequences, 
    facets, 
    totalResults, 
    searchTime, 
    isLoading, 
    error, 
    health 
  } = useTypesenseSequenceFilter({
    query: filters.query,
    filters: {
      // Add user_id filter to only show this user's sequences
      user_id: [userId],
      software: filters.software,
      holidays: filters.holiday,
      genres: filters.genres,
      artists: filters.artists,
      minPrice: filters.minPrice,
      maxPrice: filters.maxPrice,
      featured: filters.featured,
      isFree: filters.isFree,
      fileFormats: filters.fileFormats,
      albums: filters.albums,
    },
    sort: filters.sort,
    page: currentPage,
    per_page: ITEMS_PER_PAGE,
  });

  // Update URL when filters change (but don't change the route, just query params)
  useEffect(() => {
    const params = new URLSearchParams();
    
    if (filters.query) params.set('q', filters.query);
    if (filters.software.length > 0) params.set('software', filters.software.join(','));
    if (filters.holiday.length > 0) params.set('holiday', filters.holiday.join(','));
    if (filters.genres.length > 0) params.set('genres', filters.genres.join(','));
    if (filters.artists.length > 0) params.set('artists', filters.artists.join(','));
    if (filters.minPrice !== undefined) params.set('minPrice', filters.minPrice.toString());
    if (filters.maxPrice !== undefined) params.set('maxPrice', filters.maxPrice.toString());
    if (filters.featured !== undefined) params.set('featured', filters.featured.toString());
    if (filters.isFree !== undefined) params.set('free', filters.isFree.toString());
    if (filters.fileFormats && filters.fileFormats.length > 0) params.set('fileFormats', filters.fileFormats.join(','));
    if (filters.albums && filters.albums.length > 0) params.set('albums', filters.albums.join(','));
    if (filters.sort !== SEARCH.DEFAULT_SORT) params.set('sort', filters.sort);
    if (currentPage > 1) params.set('page', currentPage.toString());

    const newSearch = params.toString();
    if (newSearch !== searchParams.toString()) {
      navigate({ search: newSearch }, { replace: true });
    }
  }, [filters, currentPage, navigate, searchParams]);

  // Handle search input
  const handleQueryChange = (query: string) => {
    setFilters(prev => ({ ...prev, query }));
    setCurrentPage(1);
  };

  // Filter handlers
  const toggleArrayFilter = (filterType: keyof Pick<SearchFilters, 'software' | 'holiday' | 'genres' | 'artists'>, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType].includes(value)
        ? prev[filterType].filter(item => item !== value)
        : [...prev[filterType], value]
    }));
    setCurrentPage(1);
  };

  const toggleExtraArrayFilter = (filterType: keyof Pick<SearchFilters, 'fileFormats' | 'albums'>, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: (prev[filterType] || []).includes(value)
        ? (prev[filterType] || []).filter((v: string) => v !== value)
        : ([...(prev[filterType] || []), value] as any)
    }));
    setCurrentPage(1);
  };

  const toggleBooleanFilter = (filterType: 'featured' | 'isFree') => {
    setFilters(prev => ({
      ...prev,
      [filterType]: prev[filterType] === true ? undefined : true
    }));
    setCurrentPage(1);
  };

  const setPriceRange = (min?: number, max?: number) => {
    setFilters(prev => ({
      ...prev,
      minPrice: min,
      maxPrice: max
    }));
    setCurrentPage(1);
  };

  const clearAllFilters = () => {
    setFilters({
      query: '',
      software: [],
      holiday: [],
      genres: [],
      artists: [],
      minPrice: undefined,
      maxPrice: undefined,
      featured: undefined,
      isFree: undefined,
      fileFormats: [],
      albums: [],
      sort: SEARCH.DEFAULT_SORT,
    });
    setCurrentPage(1);
  };

  const toggleFacetsExpanded = (facet: string) => {
    setFacetsExpanded(prev => ({
      ...prev,
      [facet]: !prev[facet as keyof typeof prev]
    }));
  };

  // Check if any filters are active
  const hasActiveFilters = filters.query || 
    filters.software.length > 0 || 
    filters.holiday.length > 0 || 
    filters.genres.length > 0 || 
    filters.artists.length > 0 || 
    filters.fileFormats?.length || 
    filters.albums?.length || 
    filters.minPrice !== undefined || 
    filters.maxPrice !== undefined || 
    filters.featured || 
    filters.isFree;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-2xl font-bold">
            {isOwner ? 'My Sequences' : 'Sequences'}
          </h3>
          <p className="text-muted-foreground">
            {isLoading ? 'Loading...' : `${totalResults} sequence${totalResults !== 1 ? 's' : ''} ${hasActiveFilters ? 'found' : 'created'}`}
            {searchTime > 0 && ` (${searchTime}ms)`}
          </p>
        </div>

        {/* Mobile filter toggle */}
        <div className="lg:hidden">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <SlidersHorizontal className="w-4 h-4 mr-2" />
            Filters
            {hasActiveFilters && (
              <span className="ml-2 bg-primary text-primary-foreground rounded-full px-2 py-0.5 text-xs">
                Active
              </span>
            )}
          </Button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder={`Search ${username}'s sequences...`}
          className="pl-10 pr-10"
          value={filters.query}
          onChange={(e) => handleQueryChange(e.target.value)}
        />
        {filters.query && (
          <Button
            variant="ghost"
            size="sm"
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
            onClick={() => handleQueryChange('')}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="flex gap-6">
        {/* Filter Sidebar */}
        <div className={`${showFilters ? 'block' : 'hidden'} lg:block w-full lg:w-80 shrink-0`}>
          <SequenceFilterSidebar
            filters={filters}
            facets={facets}
            facetsExpanded={facetsExpanded}
            artistSearchQuery={artistSearchQuery}
            albumSearchQuery={albumSearchQuery}
            onFiltersChange={setFilters}
            onToggleArrayFilter={toggleArrayFilter}
            onToggleExtraArrayFilter={toggleExtraArrayFilter}
            onToggleBooleanFilter={toggleBooleanFilter}
            onSetPriceRange={setPriceRange}
            onClearAllFilters={clearAllFilters}
            onToggleFacetsExpanded={toggleFacetsExpanded}
            onArtistSearchChange={setArtistSearchQuery}
            onAlbumSearchChange={setAlbumSearchQuery}
          />
        </div>

        {/* Results */}
        <div className="flex-1 min-w-0">
          {error && (
            <div className="text-center py-12">
              <p className="text-destructive">Error loading sequences: {error}</p>
            </div>
          )}

          {!isLoading && !error && sequences.length === 0 && !hasActiveFilters && (
            <div className="text-center py-12">
              {isOwner ? (
                <>
                  <h2 className="text-2xl font-bold mb-4">Share Your First Sequence</h2>
                  <p className="text-muted-foreground mb-6">
                    Upload your holiday light sequences and share them with the community!
                  </p>
                  <Button asChild size="lg">
                    <Link to="/upload-sequence">
                      <PlusCircle className="mr-2" size={16} />
                      Upload Your First Sequence
                    </Link>
                  </Button>
                </>
              ) : (
                <p className="text-muted-foreground">No sequences created yet</p>
              )}
            </div>
          )}

          {!isLoading && !error && sequences.length === 0 && hasActiveFilters && (
            <div className="text-center py-12">
              <p className="text-muted-foreground mb-4">
                No sequences found matching your filters
              </p>
              <Button variant="outline" onClick={clearAllFilters}>
                Clear All Filters
              </Button>
            </div>
          )}

          {sequences.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sequences.map((sequence) => (
                <SequenceCard key={sequence.id} sequence={sequence} />
              ))}
            </div>
          )}

          {/* Loading skeleton */}
          {isLoading && (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-square bg-muted rounded-xl mb-3"></div>
                  <div className="h-4 bg-muted rounded mb-2"></div>
                  <div className="h-3 bg-muted rounded w-3/4"></div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination could be added here if needed */}
        </div>
      </div>
    </div>
  );
};

export default ProfileSequencesTab;